import classNames from "classnames";
import { Suspense, createContext, useRef, useState } from "react";
import { type ContextState, useContextState } from "hooks/use-context-state";
import { type QuoteDto } from "types/quote-types";
import { type PaymentService } from "utils/api/payment";
import { type QuotePaymentResponse } from "utils/api/quote";
import { Checkout } from "./checkout";
import { type CardNumberFieldIcon } from "./credit-card-form";
import { type CreditCardFormChangeListener } from "./credit-card-form/utils";
import { PayPal } from "./paypal";
import { ProviderManager } from "./provider-manager";
import { Stripe } from "./stripe";
import { ButtonsRow, Container } from "./styles";
import { Test } from "./test";

export interface CreditCardFieldState {
  isDirty: boolean;
  isEmpty: boolean;
  isFocused: boolean;
  isValid: boolean;
}

export interface CreditCardExtraFieldState extends CreditCardFieldState {
  value: string;
}

export type Component = `${PaymentService}${"Button" | "CreditCard"}`;

export type PaymentMethod =
  | "applePay"
  | "creditCard"
  | "googlePay"
  | "payPal"
  | "test";

export type ProviderStatus = "failed" | "loaded";

export interface PaymentFormProps {
  className?: string;
  disabled: boolean | Record<Exclude<PaymentMethod, "test">, boolean>;
  onCreditCardChange: CreditCardFormChangeListener;
  onError: (error: unknown) => void;
  onPaymentComplete: (quotePaymentResponse?: QuotePaymentResponse) => void;
  onPaymentError: (error: unknown, silent?: boolean) => void;
  onPaymentRequest: (method: PaymentMethod) => void;
  onReady: () => void;
  paymentData: { quoteId?: number; totalPrice: number } & (
    | { amendPaymentHash: string; policyCodeHash?: string; quoteDto?: never }
    | { amendPaymentHash?: never; quoteDto: QuoteDto }
  );
  paypal?: boolean;
  provider: Omit<PaymentService, "payPal"> | null;
  showCreditCardExtraFields: boolean;
  test: boolean;
}

export const PaymentFormContext = createContext<{
  amendPaymentHash?: string;
  cardNumberFieldIcon?: ContextState<CardNumberFieldIcon>;
  creditCardFields?: {
    cardNumber: ContextState<CreditCardFieldState>;
    expiryDate: ContextState<CreditCardFieldState>;
    extraFields?: {
      nameOnCard: ContextState<CreditCardExtraFieldState>;
      postalCode: ContextState<CreditCardExtraFieldState>;
    };
    securityCode: ContextState<CreditCardFieldState>;
  };
  onComponentReady?: (component: Component) => void;
  onComponentRequest?: (component: Component) => void;
  onPaymentComplete?: (quotePaymentResponse?: QuotePaymentResponse) => void;
  onPaymentError?: (error: unknown, silent?: boolean) => void;
  onPaymentRequest?: (method: PaymentMethod) => void;
  onProviderError?: (error: unknown) => void;
  onProviderLoad?: (provider: PaymentService) => void;
  onProviderRequest?: (provider: PaymentService) => void;
  policyCodeHash?: string;
  providerStatus?: ProviderStatus;
  quoteDto?: QuoteDto;
  quoteId?: number;
  totalPrice?: number;
}>({});

export function PaymentForm({
  className,
  disabled,
  onCreditCardChange,
  onError,
  onPaymentComplete,
  onPaymentError,
  onPaymentRequest,
  onReady,
  paymentData,
  paypal = true,
  provider,
  showCreditCardExtraFields,
  test,
}: PaymentFormProps) {
  const componentIsReady = useRef<Partial<Record<Component, boolean>>>({});
  const providerIsLoaded = useRef<Partial<Record<PaymentService, boolean>>>({});
  const [providerStatus, setProviderStatus] = useState<ProviderStatus>();

  const cardNumberFieldIcon = useContextState<CardNumberFieldIcon>(null);

  const creditCardExtraFields = {
    nameOnCard: useContextState<CreditCardExtraFieldState>({
      isDirty: false,
      isEmpty: true,
      isFocused: false,
      isValid: false,
      value: "",
    }),
    postalCode: useContextState<CreditCardExtraFieldState>({
      isDirty: false,
      isEmpty: true,
      isFocused: false,
      isValid: false,
      value: "",
    }),
  };

  const creditCardFields = {
    cardNumber: useContextState<CreditCardFieldState>({
      isDirty: false,
      isEmpty: true,
      isFocused: false,
      isValid: false,
    }),
    expiryDate: useContextState<CreditCardFieldState>({
      isDirty: false,
      isEmpty: true,
      isFocused: false,
      isValid: false,
    }),
    securityCode: useContextState<CreditCardFieldState>({
      isDirty: false,
      isEmpty: true,
      isFocused: false,
      isValid: false,
    }),
    ...(showCreditCardExtraFields && { extraFields: creditCardExtraFields }),
  };

  const { Button, CreditCard, Provider } =
    provider === "checkOut" ? Checkout : provider === "stripe" ? Stripe : {};

  return (
    <Suspense>
      <PaymentFormContext.Provider
        value={{
          cardNumberFieldIcon,
          creditCardFields,
          onComponentReady: (component) => {
            if (componentIsReady.current[component] === false) {
              componentIsReady.current[component] = true;
            }
            if (Object.values(componentIsReady.current).every(Boolean)) {
              onReady();
            }
          },
          onComponentRequest: (component) => {
            componentIsReady.current[component] = false;
          },
          onPaymentComplete,
          onPaymentError,
          onPaymentRequest,
          onProviderError: (error) => {
            if (providerStatus !== "failed") {
              setProviderStatus("failed");
            }
            onError(error);
          },
          onProviderLoad: (provider) => {
            if (providerIsLoaded.current[provider] === false) {
              providerIsLoaded.current[provider] = true;
            }
            if (
              providerStatus !== "failed" &&
              Object.values(providerIsLoaded.current).every(Boolean)
            ) {
              setProviderStatus("loaded");
            }
          },
          onProviderRequest: (provider) => {
            providerIsLoaded.current[provider] = false;
          },
          providerStatus,
          ...paymentData,
        }}
      >
        {test ? (
          <Test
            className={className}
            onLoad={() => {
              setProviderStatus("loaded");
              onReady();
            }}
          />
        ) : (
          <ProviderManager
            BaseProvider={Provider ?? null}
            PayPalProvider={paypal ? PayPal.Provider : null}
          >
            {providerStatus === "loaded" && (
              <Container
                className={classNames(className, { disabled: !!disabled })}
                {...(!!disabled && { inert: "" })}
              >
                <ButtonsRow>
                  {Button && (
                    <Button
                      disabled={
                        disabled &&
                        (disabled === true ||
                          (disabled.applePay && disabled.googlePay))
                      }
                    />
                  )}
                  {paypal &&
                    !!import.meta.env.VITE_PAYPAL_CLIENT_ID &&
                    import.meta.env.VITE_PAYPAL_CLIENT_ID.length > 0 && (
                      <PayPal.Button
                        disabled={
                          disabled && (disabled === true || disabled.payPal)
                        }
                      />
                    )}
                </ButtonsRow>
                {CreditCard && (
                  <CreditCard
                    disabled={
                      disabled && (disabled === true || disabled.creditCard)
                    }
                    onChange={onCreditCardChange}
                  />
                )}
              </Container>
            )}
          </ProviderManager>
        )}
      </PaymentFormContext.Provider>
    </Suspense>
  );
}
